import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { ColorEnum } from '../api/categories';
import { useCategories } from '../api/categories';
import { ApiError } from '../api/client';
import { useEvent } from '../api/events';
import { useSetInventoryTracking } from '../api/inventory';
import type { Product } from '../api/products';
import {
  useArchiveProduct,
  useCreateProduct,
  usePatchProduct,
  useRestoreProduct,
} from '../api/products';
import { Field } from '../components/Field';
import { Modal } from '../components/Modal';

const COLORS: ColorEnum[] = ['CORAL', 'TEAL', 'GREEN', 'LILAC', 'PURPLE'];

const COLOR_VAR: Record<ColorEnum, string> = {
  CORAL: 'var(--oeg-coral)',
  TEAL: 'var(--oeg-teal)',
  GREEN: 'var(--oeg-green)',
  LILAC: 'var(--oeg-lilac)',
  PURPLE: 'var(--oeg-purple)',
};

const productSchema = z.object({
  name: z.string().min(1, 'Required').max(120, 'Max 120 characters'),
  categoryId: z.string().min(1, 'Required'),
  sku: z.string().max(64, 'Max 64 characters').nullable(),
  price: z
    .string()
    .regex(/^\d+(\.\d{1,2})?$/, 'Enter a price like 12.50'),
  subtitle: z.string().max(120, 'Max 120 characters').nullable(),
  description: z.string().max(2000, 'Max 2000 characters').nullable(),
  thumbColor: z.enum(['CORAL', 'TEAL', 'GREEN', 'LILAC', 'PURPLE']),
  thumbLabel: z.string().min(1, 'Required').max(4, 'Max 4 characters'),
});

type ProductValues = z.infer<typeof productSchema>;

function deriveThumbLabel(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '';
  return trimmed.slice(0, 2).toUpperCase();
}

export interface ProductModalProps {
  eventId: string;
  mode: 'create' | 'edit';
  product: Product | null;
  defaultCategoryId: string | null;
  onClose: () => void;
  /**
   * Whether the starting count may still be corrected — false once the product
   * has a sale or hold-release. Advisory: the server enforces the same rule.
   */
  startingEditable?: boolean;
}

export function ProductModal({
  eventId,
  mode,
  product,
  defaultCategoryId,
  onClose,
  startingEditable = true,
}: ProductModalProps) {
  const eventQuery = useEvent(eventId);
  const categoriesQuery = useCategories(eventId);
  const createProduct = useCreateProduct(eventId);
  const patchProduct = usePatchProduct(eventId);
  const archiveProduct = useArchiveProduct(eventId);
  const restoreProduct = useRestoreProduct(eventId);
  const setTracking = useSetInventoryTracking(eventId);

  // Local-only inventory state for the toggle/starting fields. Persisted via
  // a separate PATCH /inventory call after the main product save, since the
  // main /products endpoint doesn't accept inventory params.
  const [trackInv, setTrackInv] = useState<boolean>(product?.inventoryTracked ?? false);
  const [startingInv, setStartingInv] = useState<string>(
    product?.inventoryStarting != null ? String(product.inventoryStarting) : '',
  );
  const [confirmDisable, setConfirmDisable] = useState<boolean>(false);

  const [topLevelError, setTopLevelError] = useState<string | null>(null);
  const [skuError, setSkuError] = useState<string | null>(null);
  const thumbManuallyEdited = useRef<boolean>(mode === 'edit');

  const activeCategories = useMemo(
    () => (categoriesQuery.data ?? []).filter((c) => !c.archivedAt),
    [categoriesQuery.data],
  );

  const initialCategoryId =
    product?.categoryId ??
    defaultCategoryId ??
    activeCategories[0]?.id ??
    '';

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ProductValues>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      name: product?.name ?? '',
      categoryId: initialCategoryId,
      sku: product?.sku ?? '',
      price: product ? (product.priceCents / 100).toFixed(2) : '',
      subtitle: product?.subtitle ?? '',
      description: product?.description ?? '',
      thumbColor: product?.thumbColor ?? 'CORAL',
      thumbLabel: product?.thumbLabel ?? '',
    },
  });

  const selectedColor = watch('thumbColor');
  const nameValue = watch('name');
  const thumbLabelValue = watch('thumbLabel');

  // Auto-derive thumb label in create mode unless the user has touched it.
  useEffect(() => {
    if (mode !== 'create') return;
    if (thumbManuallyEdited.current) return;
    const derived = deriveThumbLabel(nameValue ?? '');
    setValue('thumbLabel', derived, { shouldDirty: true });
  }, [nameValue, mode, setValue]);

  // Update categoryId once categories load. The form's defaultValue won't bind
  // to a <select> option that didn't exist at mount, so re-set it explicitly.
  useEffect(() => {
    if (activeCategories.length === 0) return;
    const desired =
      product?.categoryId ?? defaultCategoryId ?? activeCategories[0]?.id ?? '';
    if (desired) setValue('categoryId', desired);
    // Run once per (categories, prefill) change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategories.length]);

  function onThumbLabelInput(e: React.ChangeEvent<HTMLInputElement>) {
    thumbManuallyEdited.current = true;
    setValue('thumbLabel', e.target.value.slice(0, 4), { shouldDirty: true });
  }

  const currency = eventQuery.data?.currency ?? 'USD';

  const onSubmit = handleSubmit(async (values) => {
    setTopLevelError(null);
    setSkuError(null);
    const priceCents = Math.round(parseFloat(values.price) * 100);
    const skuValue = values.sku && values.sku.trim() !== '' ? values.sku : null;
    const subtitleValue =
      values.subtitle && values.subtitle.trim() !== '' ? values.subtitle : null;
    const descriptionValue =
      values.description && values.description.trim() !== ''
        ? values.description
        : null;

    try {
      let savedId: string | null = null;
      if (mode === 'create') {
        const created = await createProduct.mutateAsync({
          categoryId: values.categoryId,
          name: values.name,
          sku: skuValue,
          priceCents,
          subtitle: subtitleValue,
          description: descriptionValue,
          thumbLabel: values.thumbLabel,
          thumbColor: values.thumbColor,
        });
        savedId = created.id;
      } else if (product) {
        const updated = await patchProduct.mutateAsync({
          id: product.id,
          body: {
            categoryId: values.categoryId,
            name: values.name,
            sku: skuValue,
            priceCents,
            subtitle: subtitleValue,
            description: descriptionValue,
            thumbLabel: values.thumbLabel,
            thumbColor: values.thumbColor,
          },
        });
        savedId = updated.id;
      }

      // Inventory: only call PATCH /inventory when state actually changed
      // vs the existing product, or when creating with tracking enabled.
      // Editing the starting count on an already-tracked product is allowed
      // while its ledger is clean (a receiving correction); the server answers
      // 409 inventory_locked once a sale or hold-release exists.
      const wasTracked = product?.inventoryTracked ?? false;
      const wasStarting = product?.inventoryStarting ?? null;
      if (savedId) {
        if (trackInv && !wasTracked) {
          const startingNum = Number.parseInt(startingInv || '0', 10);
          if (!Number.isFinite(startingNum) || startingNum < 0) {
            setTopLevelError('Starting quantity must be a non-negative integer.');
            return;
          }
          await setTracking.mutateAsync({
            productId: savedId,
            inventoryTracked: true,
            inventoryStarting: startingNum,
          });
        } else if (!trackInv && wasTracked) {
          await setTracking.mutateAsync({
            productId: savedId,
            inventoryTracked: false,
          });
        } else if (
          trackInv &&
          wasTracked &&
          Number.parseInt(startingInv || '0', 10) !== wasStarting
        ) {
          const startingNum = Number.parseInt(startingInv || '0', 10);
          if (!Number.isFinite(startingNum) || startingNum < 0) {
            setTopLevelError('Starting quantity must be a non-negative integer.');
            return;
          }
          await setTracking.mutateAsync({
            productId: savedId,
            inventoryTracked: true,
            inventoryStarting: startingNum,
          });
        }
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'duplicate_sku') {
        setSkuError('SKU already in use.');
      } else if (err instanceof ApiError && err.code === 'inventory_locked') {
        setTopLevelError('Sales have been recorded — use Adjust from the row menu instead.');
      } else if (err instanceof ApiError && err.code === 'invalid_request') {
        setTopLevelError("Some fields aren't valid — check your input.");
      } else {
        setTopLevelError('Something went wrong. Try again.');
      }
    }
  });

  async function onArchiveClick() {
    if (!product) return;
    try {
      await archiveProduct.mutateAsync(product.id);
      onClose();
    } catch {
      setTopLevelError('Could not archive. Try again.');
    }
  }

  async function onRestoreClick() {
    if (!product) return;
    try {
      await restoreProduct.mutateAsync(product.id);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'duplicate_sku') {
        // The partial unique SKU index only covers active rows, so an active
        // product may have claimed this SKU while it was archived.
        setTopLevelError('SKU is now in use by an active product — rename it before restoring.');
      } else {
        setTopLevelError('Could not restore. Try again.');
      }
    }
  }

  const title = mode === 'create' ? 'New product' : (product?.name ?? 'Edit product');
  const sub =
    mode === 'create'
      ? 'Goes live to floor devices on save'
      : product?.sku
        ? product.sku
        : '';

  return (
    <Modal
      title={title}
      sub={sub || undefined}
      width={620}
      onClose={onClose}
      footer={
        <>
          {mode === 'edit' &&
            (product?.archivedAt ? (
              <button
                type="button"
                className="btn btn-ghost"
                style={{ marginRight: 'auto' }}
                onClick={() => void onRestoreClick()}
                data-testid="restore-product"
              >
                Restore
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-ghost"
                style={{ marginRight: 'auto', color: 'var(--oeg-danger)' }}
                onClick={() => void onArchiveClick()}
                data-testid="archive-product"
              >
                Archive
              </button>
            ))}
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? 'Saving…'
              : mode === 'create'
                ? 'Create'
                : 'Save changes'}
          </button>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate style={{ display: 'grid', gap: 16 }}>
        <Field label="Name" error={errors.name?.message}>
          <input type="text" maxLength={120} {...register('name')} />
        </Field>
        <div className="grid-2">
          <Field label="Category" error={errors.categoryId?.message}>
            <select {...register('categoryId')}>
              {activeCategories.length === 0 && <option value="">—</option>}
              {activeCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="SKU" error={skuError ?? errors.sku?.message ?? undefined}>
            <input type="text" maxLength={64} {...register('sku')} />
          </Field>
        </div>
        <div className="grid-2">
          <Field label={`Price (${currency})`} error={errors.price?.message}>
            <input type="text" inputMode="decimal" {...register('price')} />
          </Field>
          <Field
            label="Thumb label"
            error={errors.thumbLabel?.message}
            help="Auto-derived from name; max 4 characters."
          >
            <input
              type="text"
              maxLength={4}
              value={thumbLabelValue ?? ''}
              onChange={onThumbLabelInput}
              name="thumbLabel"
            />
          </Field>
        </div>
        <Field label="Subtitle" error={errors.subtitle?.message}>
          <input type="text" maxLength={120} {...register('subtitle')} />
        </Field>
        <Field
          label="Description"
          error={errors.description?.message}
          help="Back-office only; not shown on the floor catalog."
        >
          <textarea rows={3} maxLength={2000} {...register('description')} />
        </Field>
        <div
          style={{
            border: '1px solid var(--border-1)',
            borderRadius: 8,
            padding: 12,
            background: 'var(--bg-surface-1)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>Track inventory</div>
              <div style={{ color: 'var(--fg-3)', fontSize: 12 }}>
                {trackInv
                  ? 'Server enforces stock; sales decrement remaining.'
                  : 'Unlimited; no overselling guard.'}
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={trackInv}
              className={`toggle${trackInv ? ' on' : ''}`}
              onClick={() => {
                if (trackInv && product?.inventoryTracked) {
                  setConfirmDisable(true);
                } else {
                  setTrackInv(!trackInv);
                }
              }}
              data-testid="track-inventory-toggle"
            />
          </div>
          {trackInv && (
            <div style={{ marginTop: 12 }}>
              <Field
                label="Starting quantity"
                help={
                  !product?.inventoryTracked
                    ? 'Sets both starting and remaining when tracking is enabled.'
                    : startingEditable
                      ? 'Correct the count before sales start. Sets both starting and remaining.'
                      : 'Sales recorded — use Adjust from the row menu.'
                }
              >
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={startingInv}
                  onChange={(e) => setStartingInv(e.target.value)}
                  disabled={product?.inventoryTracked === true && !startingEditable}
                  data-testid="inventory-starting"
                />
              </Field>
            </div>
          )}
          {confirmDisable && (
            <div
              style={{
                marginTop: 10,
                padding: 10,
                borderRadius: 6,
                background: 'var(--bg-surface-2)',
                fontSize: 12,
              }}
            >
              Disabling tracking will clear the current stock counts. Are you sure?
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setConfirmDisable(false)}
                >
                  Keep tracking on
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => {
                    setTrackInv(false);
                    setConfirmDisable(false);
                  }}
                >
                  Disable tracking
                </button>
              </div>
            </div>
          )}
        </div>
        <Field label="Tile color" error={errors.thumbColor?.message}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {COLORS.map((c) => {
              const active = selectedColor === c;
              return (
                <button
                  key={c}
                  type="button"
                  aria-label={`Color ${c}`}
                  aria-pressed={active}
                  data-testid={`thumb-color-${c}`}
                  onClick={() => setValue('thumbColor', c, { shouldDirty: true })}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: COLOR_VAR[c],
                    border: active ? '2px solid var(--fg-1)' : '1px solid var(--border-1)',
                    boxShadow: active
                      ? '0 0 0 2px var(--bg-app), 0 0 0 4px var(--oeg-purple)'
                      : 'none',
                    cursor: 'pointer',
                  }}
                />
              );
            })}
          </div>
        </Field>
        <input type="hidden" {...register('thumbColor')} />
        {topLevelError && <div className="form-error">{topLevelError}</div>}
      </form>
    </Modal>
  );
}
